/**
 * Os dados da ficha de um ponto: os blocos da série dos modelos (da rodada em
 * uso, a mesma do mapa), o nome do lugar e o fuso. Com o acesso de pesquisa,
 * também os cenários do WeatherNext 2 (chance de chuva e a faixa da temperatura).
 */
import { lonLatToMap } from '../map/projection';
import { ensureDataSource } from '../vectors/loader';
import { carregarBloco, modeloTempoEmUso, type IndicePrevisao, type Modelo, type ModeloId, type ModeloTempo } from './api';
import { rodadaEmUso } from './motor';
import {
  blocosDoPonto,
  chaveBloco,
  fusoDoLugar,
  gradeDeTempo,
  noInstante,
  posicao,
  serieNoPonto,
  serieNoPontoDe,
  taxaNoIntervalo,
  valorDoIntervalo,
  type ChanceDe24h,
  type SeriesDoPonto,
} from './ponto';
import { instantesDaRodada, primeiroPassoVisivel } from './tempo';

/** Blocos baixados (imutáveis por rodada): uma ficha reaberta não baixa de novo. */
const blocos = new Map<string, Promise<Uint16Array>>();
const MAX_BLOCOS = 40;

function bloco(m: Modelo, rodada: string, bi: number, bj: number): Promise<Uint16Array> {
  const chave = `${m.id}/${rodada}/${bi}_${bj}`;
  let p = blocos.get(chave);
  if (!p) {
    const novo = carregarBloco(m, rodada, bi, bj);
    novo.catch(() => {
      if (blocos.get(chave) === novo) blocos.delete(chave);
    });
    p = novo;
    blocos.set(chave, p);
    while (blocos.size > MAX_BLOCOS) blocos.delete(blocos.keys().next().value!);
  }
  return p;
}

export interface Lugar {
  nome: string | null;
  uf: string | null;
  fuso: number;
}

/** O município do ponto (a malha do IBGE que o app já tem), ou só o fuso pela longitude. */
export async function lugarDoPonto(lon: number, lat: number): Promise<Lugar> {
  try {
    const fonte = await ensureDataSource('municipios');
    const [x, y] = lonLatToMap(lon, lat);
    for (const f of fonte?.getFeaturesAtCoordinate([x, y]) ?? []) {
      const nome = f.get('nome');
      const uf = f.get('uf');
      if (typeof nome === 'string' && nome) {
        const u = typeof uf === 'string' ? uf : null;
        return { nome, uf: u, fuso: fusoDoLugar(u, lon) };
      }
    }
  } catch {
    // Sem a malha, o lugar fica só com as coordenadas.
  }
  return { nome: null, uf: null, fuso: fusoDoLugar(null, lon) };
}

export interface DadosDoPonto {
  series: SeriesDoPonto;
  /** O modelo do tempo das séries (IFS ou AIFS). */
  tempo: ModeloTempo;
  /** O modelo do tempo tem rajada (o AIFS não tem). */
  temRajada: boolean;
  /** O passo da grade do modelo do tempo (graus): a área que cada valor representa. */
  passo: number;
  rodadas: Partial<Record<ModeloId, string>>;
  /** Último instante com previsão do CAMS (a faixa da fumaça acaba ali). */
  fimCams: number | null;
  /** A chance de 24 h do WeatherNext 2 (para os dias), com o acesso de pesquisa. */
  cenarios: ChanceDe24h | null;
}

/** Séries de um modelo no ponto, na grade dos passos dele. */
async function seriesDoModelo(m: Modelo, rodada: string, lon: number, lat: number, vars: string[], semDado: number): Promise<Record<string, Float32Array> | null> {
  const p = posicao(m.grade, lon, lat);
  if (!p) return null;
  const precisos = blocosDoPonto(m.grade, p[0], p[1]);
  const lidos = await Promise.all(precisos.map(([bi, bj]) => bloco(m, rodada, bi, bj)));
  const mapa = new Map(precisos.map(([bi, bj], k) => [chaveBloco(bi, bj), lidos[k]!]));
  const series: Record<string, Float32Array> = Object.fromEntries(vars.map((v) => [v, serieNoPonto(m, mapa, v, p[0], p[1], semDado)]));
  // Vento: a velocidade em cada ponto da grade, interpolada depois (como o mapa).
  if (vars.includes('u10') && vars.includes('v10')) {
    series.velocidade = serieNoPontoDe(m, mapa, ['u10', 'v10'], ([u, v]) => Math.hypot(u!, v!), p[0], p[1], semDado);
  }
  return series;
}

/**
 * Tudo da ficha numa grade de 3 h, de agora até o fim do modelo do tempo (IFS
 * ou AIFS; nos passos de 6 h, o instante é interpolado e o intervalo herdado).
 * Rejeita se o ponto está fora do recorte ou o índice não tem o modelo.
 */
export async function carregarPonto(
  indice: IndicePrevisao,
  fixadas: Partial<Record<ModeloId, string>>,
  lon: number,
  lat: number,
  agora = Date.now(),
  escolhido: ModeloTempo = 'ifs',
): Promise<DadosDoPonto> {
  const tempo = modeloTempoEmUso(escolhido, indice);
  const ifs = indice.modelos[tempo];
  const rIfs = rodadaEmUso(ifs, fixadas[tempo]);
  if (!ifs || !rIfs) throw new Error(`sem rodada do ${tempo.toUpperCase()}`);
  const temRajada = 'rajada' in ifs.vars;
  const cams = indice.modelos.cams;
  const rCams = rodadaEmUso(cams, fixadas.cams);
  const wn2 = indice.modelos.wn2;
  const rWn2 = rodadaEmUso(wn2, fixadas.wn2);
  const [si, sc, sw] = await Promise.all([
    seriesDoModelo(ifs, rIfs, lon, lat, ['t2m', 'ur', 'chuva', 'u10', 'v10', ...(temRajada ? ['rajada'] : [])], indice.semDado),
    cams && rCams ? seriesDoModelo(cams, rCams, lon, lat, ['pm25'], indice.semDado).catch(() => null) : Promise.resolve(null),
    // Os cenários são um acréscimo: sem eles (falha, sem acesso), a ficha é a de sempre.
    wn2 && rWn2 ? seriesDoModelo(wn2, rWn2, lon, lat, ['chance', 't10', 't90', 'chance24'], indice.semDado).catch(() => null) : Promise.resolve(null),
  ]);
  if (!si) throw new Error('ponto fora da área da previsão');
  const instIfs = instantesDaRodada(ifs, rIfs);
  const inicio = instIfs[primeiroPassoVisivel(instIfs, agora)]!;
  const t = gradeDeTempo(inicio, instIfs[instIfs.length - 1]!);
  const vento = Float32Array.from(si.velocidade!, (x) => x * 3.6);
  // Para onde o vento vai (graus a partir do norte), das componentes interpoladas: só a seta.
  const rumo = Float32Array.from(instIfs, (_, k) => (Math.atan2(si.u10![k]!, si.v10![k]!) * 180) / Math.PI);
  const instCams = cams && rCams ? instantesDaRodada(cams, rCams) : [];
  const instWn2 = sw && wn2 && rWn2 ? instantesDaRodada(wn2, rWn2) : [];
  const em = (inst: number[], v: ArrayLike<number>) => Float32Array.from(t, (x) => noInstante(inst, v, x));
  return {
    series: {
      t,
      t2m: em(instIfs, si.t2m!),
      ur: em(instIfs, si.ur!),
      vento: em(instIfs, vento),
      rumo: Float32Array.from(t, (x) => {
        const k = instIfs.indexOf(x);
        return k >= 0 ? rumo[k]! : NaN;
      }),
      // Chuva e rajada são do intervalo: a fatia de 3 h herda o valor do intervalo que a contém.
      chuva: Float32Array.from(t, (x) => taxaNoIntervalo(instIfs, si.chuva!, x)),
      rajada: si.rajada ? Float32Array.from(t, (x) => valorDoIntervalo(instIfs, si.rajada!, x) * 3.6) : new Float32Array(t.length).fill(NaN),
      pm25: sc ? em(instCams, sc.pm25!) : new Float32Array(t.length).fill(NaN),
      ...(sw
        ? {
            // A chance é do intervalo de 6 h: a fatia de 3 h herda a do intervalo que a contém.
            chance: Float32Array.from(t, (x) => valorDoIntervalo(instWn2, sw.chance!, x)),
            t10: em(instWn2, sw.t10!),
            t90: em(instWn2, sw.t90!),
          }
        : {}),
    },
    tempo,
    temRajada,
    passo: ifs.grade.passo,
    rodadas: { [tempo]: rIfs, ...(rCams ? { cams: rCams } : {}), ...(sw && rWn2 ? { wn2: rWn2 } : {}) },
    fimCams: instCams.length ? instCams[instCams.length - 1]! : null,
    cenarios: sw ? { instantes: instWn2, chance24: sw.chance24! } : null,
  };
}

